import axiosInstance from 'integration/api/axiosInstance';
import {
  CommonEntitiesListResponse,
  ExternalProfessionalCreationDto,
  ExternalProfessionalResponseDto,
  ExternalProfessionalUpdateDto,
} from '@docbay/schemas';
import { GetImportedDoctorsParams } from 'common/types/importedDoctors';

export const ImportProfessionalsApi = {
  /**
   * Fetches the list of imported professionals.
   *
   * @param params - The parameters for filtering and pagination.
   * @returns A promise that resolves to a response containing the list of professionals.
   */
  fetchImportedProfessionals: (params: GetImportedDoctorsParams) =>
    axiosInstance.get<
      CommonEntitiesListResponse<ExternalProfessionalResponseDto>
    >('/external/professionals', {
      params,
    }),

  /**
   * Gets an external professional by ID.
   *
   * @param id - The ID of the professional to fetch.
   * @returns A promise that resolves to a response containing the professional's data.
   */
  getExternalProfessionalById: (id: string) =>
    axiosInstance.get<ExternalProfessionalResponseDto>(
      `/external/professionals/${id}`,
    ),

  /**
   * Adds a new external professional.
   *
   * @param data - The creation data for the professional.
   * @returns A promise that resolves to a response containing the newly created professional's data.
   */
  addProfessionals: (data: ExternalProfessionalCreationDto) =>
    axiosInstance.post<ExternalProfessionalResponseDto>(
      '/external/professionals',
      data,
    ),

  /**
   * Updates an existing external professional.
   *
   * @param id - The ID of the professional to update.
   * @param data - The updated data for the professional.
   * @returns A promise that resolves to a response containing the updated professional's data.
   */
  updateProfessionals: (id: string, data: ExternalProfessionalUpdateDto) =>
    axiosInstance.patch<ExternalProfessionalResponseDto>(
      `/external/professionals/${id}`,
      data,
    ),

  /**
   * Deletes an external professional by ID.
   *
   * @param id - The ID of the professional to delete.
   * @returns A promise that resolves when the deletion is successful.
   */
  deleteProfessionals: (id: string) =>
    axiosInstance.delete(`/external/professionals/${id}`),

  /**
   * Uploads a file containing professionals' data for import.
   *
   * @param data - The form data containing the file to upload.
   * @returns A promise that resolves when the upload is successful.
   */
  uploadProfessionals: (data: FormData) =>
    axiosInstance.post('/external/professionals/import', data),

  /**
   * Gets the status of a professionals' import process.
   *
   * @returns A promise that resolves to a response containing the import status.
   */
  uploadStatus: () =>
    axiosInstance.get('/external/professionals/import/status'),
};
